/**
 * useVisibilityRefetch — trigger an immediate refetch when the tab
 * becomes visible, regains focus, or restores from iOS bfcache.
 *
 * P1 Mobile + Live-State sprint (May 13, 2026).
 *
 * Lighter sibling of `useFreshState` — for components that already
 * own their own setInterval and just need the iOS "stale on return"
 * gap closed. Adds three listeners:
 *
 *   • `visibilitychange` (document) — fires when the user tabs back.
 *   • `focus` (window) — desktop focus + iPhone Chrome.
 *   • `pageshow` (window) — iOS Safari bfcache restore (the case
 *     where neither `visibilitychange` nor `focus` fires).
 *
 * Usage:
 *   useVisibilityRefetch(() => tick(), { enabled: !!user });
 *
 * Rules:
 *   • Callback must be idempotent (it can fire twice in quick
 *     succession on iOS — visibilitychange + focus + pageshow can
 *     all land within ~50ms of each other).
 *   • Resume events are coalesced so iOS firing visibilitychange,
 *     focus and pageshow together causes one network refresh.
 *   • Online and platform refresh events close reconnect and
 *     cross-surface mutation gaps.
 */
import { useEffect, useRef } from "react";

export function useVisibilityRefetch(callback, { enabled = true, marker = "vis-refetch" } = {}) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let lastFiredAt = 0;
    const fire = (trigger) => {
      // visibilitychange fires for BOTH hidden→visible AND visible→hidden.
      // Only refetch on the visible side.
      if (trigger === "visibility" && typeof document !== "undefined" && document.hidden) {
        return;
      }
      const now = Date.now();
      if (now - lastFiredAt < 750) return;
      lastFiredAt = now;
      // eslint-disable-next-line no-console
      console.debug(`[${marker}] focus_refetch`, { trigger });
      try { cbRef.current?.(); } catch (_) { /* noop */ }
    };

    const onVisible = () => fire("visibility");
    const onFocus = () => fire("focus");
    const onPageShow = () => fire("pageshow");
    const onOnline = () => fire("online");
    const onPlatformRefresh = () => fire("platform");

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("online", onOnline);
      window.addEventListener("ndg:platform-refresh", onPlatformRefresh);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("online", onOnline);
        window.removeEventListener("ndg:platform-refresh", onPlatformRefresh);
      }
    };
  }, [enabled, marker]);
}

export default useVisibilityRefetch;
